import { LayoutGrid, LayoutList } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CircleLoader } from 'common-src/components/base';
import { ColorsNew } from 'common-src/styles';

import { getCareOrchestrationState, saveCareOrchestrationState } from 'src/features/tabsState';
import useCustomSelector from 'src/hooks/useCustomSelector';

import { departmentsInScope, hasActiveScopeFilters } from '../../scope';
import {
  deferTask,
  dismissVisitTask,
  fetchPatientsVisitHistory,
  fetchTaskQueueData,
  reopenTask,
} from '../../services/careOrchestrationApi';
import ScopeFilterGroup from '../ScopeFilterGroup';
import OpenButtons from './OpenButtons';
import StatusPills from './StatusPills';
import {
  anchorShort,
  bookableCodes,
  DEFAULT_STATES,
  formatShortDate,
  groupVisitRows,
  memberMeta,
  STATE_CHIPS,
  toVisitRow,
  VISIT_CHIPS,
  VISITS_DUE_EVIDENCE_TYPE,
} from './visitsDue';
import VisitsDueDetail from './VisitsDueDetail';
import styles from './VisitsDueView.module.scss';

const BOOKED_THIS_WEEK_DAYS = 7;

const toggle = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const LAYOUTS = [
  { key: 'list', label: 'List', Icon: LayoutList },
  { key: 'cards', label: 'Cards', Icon: LayoutGrid },
];

const VisitsDueView = ({
  organizationScope = null,
  departments = [],
  scopeFilters,
  onChangeScopeFilters,
  guideEnabled = false,
  onToggleGuide,
  isScopePaused = false,
  onToggleScopePause,
  isPinnedToCurrentScope = false,
  canResetToPinnedScope = false,
  showScopeUtilities = false,
  onToggleScopePin,
  onResetScopeToPinned,
}) => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [states, setStates] = useState(DEFAULT_STATES);
  const [visits, setVisits] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);
  const [deferringId, setDeferringId] = useState(null);
  const [reopeningId, setReopeningId] = useState(null);
  const dispatch = useDispatch();
  const savedState = useCustomSelector((state) => getCareOrchestrationState(state));
  const layout = savedState?.visitsDueLayout === 'cards' ? 'cards' : 'list';

  const scopedDepartmentIds = useMemo(
    () =>
      departmentsInScope(departments, scopeFilters, guideEnabled).map(
        (department) => `${department.id}`,
      ),
    [departments, scopeFilters, guideEnabled],
  );
  const isAllScope = isScopePaused || (!hasActiveScopeFilters(scopeFilters) && !guideEnabled);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchTaskQueueData({
      statusScope: 'open',
      organizationScope,
      departmentAthenaIds: isAllScope ? null : scopedDepartmentIds,
      evidenceType: VISITS_DUE_EVIDENCE_TYPE,
    })
      .then(async ({ taskQueueRows }) => {
        const rawRows = taskQueueRows || [];
        const patientIds = [...new Set(rawRows.map((row) => row.patientId))];
        const historyByPatientId = patientIds.length
          ? ((await fetchPatientsVisitHistory(patientIds).catch(() => null))?.data ?? {})
          : {};
        if (cancelled) return;
        setRows(
          rawRows.map((row) => {
            const ruleId = row.evidence?.sourceRef;
            const codes = bookableCodes(ruleId);
            const history = historyByPatientId[row.patientId] ?? [];
            const bookedVisit = history.find((visit) => visit.booked && codes.includes(visit.code));
            return toVisitRow({ ...row, bookedVisit, history });
          }),
        );
      })
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [organizationScope, isAllScope, scopedDepartmentIds]);

  const groups = useMemo(() => groupVisitRows(rows, states, visits), [rows, states, visits]);
  const selectedRow =
    groups.flatMap((group) => group.rows).find((row) => row.id === selectedId) ??
    groups[0]?.rows[0] ??
    null;
  const memberCount = new Set(rows.map((row) => row.patientId)).size;
  const overdue = rows.filter((row) => row.dueState === 'overdue').length;
  const due = rows.filter((row) => row.dueState === 'due').length;
  const deferred = rows.filter((row) => row.dueState === 'deferred').length;
  const bookedThisWeekCutoff = new Date(Date.now() + BOOKED_THIS_WEEK_DAYS * 24 * 60 * 60 * 1000);
  const bookedThisWeek = rows.filter(
    (row) => row.dueState === 'booked' && new Date(row.bookedVisit.date) <= bookedThisWeekCutoff,
  ).length;

  const stats = [
    { key: 'overdue', label: 'Overdue', value: overdue, tone: overdue > 0 ? 'red' : null },
    { key: 'due', label: 'Due', value: due, tone: due > 0 ? 'yellow' : null },
    { key: 'deferred', label: 'Deferred', value: deferred, tone: null },
    { key: 'booked', label: 'Booked this week', value: bookedThisWeek, tone: null },
  ];

  const selectLayout = (next) => {
    dispatch(saveCareOrchestrationState({ visitsDueLayout: next }));
  };

  const applyUpdate = (rowId, updated) =>
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? toVisitRow({
              ...r,
              heldUntil: updated.heldUntil,
              dismissedAt: updated.dismissedAt,
              deferredReason: updated.deferredReason,
              visitActivity: updated.visitActivity,
            })
          : r,
      ),
    );

  const handleDismiss = async (row, reason, note) => {
    if (dismissingId) return;
    setDismissingId(row.id);
    try {
      applyUpdate(row.id, await dismissVisitTask(row.id, reason, note));
    } finally {
      setDismissingId(null);
    }
  };

  const handleDefer = async (row, reevalOn, reason, note) => {
    if (deferringId) return;
    setDeferringId(row.id);
    try {
      const heldUntil = new Date(`${reevalOn}T00:00:00`).toISOString();
      applyUpdate(row.id, await deferTask(row.id, heldUntil, reason, note));
    } finally {
      setDeferringId(null);
    }
  };

  const handleReopen = async (row) => {
    if (reopeningId) return;
    setReopeningId(row.id);
    try {
      applyUpdate(row.id, await reopenTask(row.id));
    } finally {
      setReopeningId(null);
    }
  };

  const selectedClass = (row) => (row.id === selectedRow?.id ? styles.rowSelected : '');
  const selectedListClass = (row) => (row.id === selectedRow?.id ? styles.rowSelectedList : '');

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <ScopeFilterGroup
          departments={departments}
          filters={scopeFilters}
          onChangeFilters={onChangeScopeFilters}
          guideEnabled={guideEnabled}
          onToggleGuide={onToggleGuide}
          showUtilities={showScopeUtilities}
          isPinned={isPinnedToCurrentScope}
          onTogglePin={onToggleScopePin}
          canReset={canResetToPinnedScope}
          onReset={onResetScopeToPinned}
          isPaused={isScopePaused}
          onTogglePause={onToggleScopePause}
        />
        <span className={styles.readout}>
          {isLoading
            ? 'Loading…'
            : `${memberCount} ${memberCount === 1 ? 'member' : 'members'} due a visit`}
        </span>
      </div>

      <div className={styles.statsRow}>
        {stats.map((stat) => (
          <div key={stat.key} className={styles.statCard}>
            <div className={styles.statLabel}>{stat.label}</div>
            <span
              className={`${styles.statValue} ${stat.tone ? styles[`statValue${stat.tone}`] : ''}`}
            >
              {stat.value}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.layoutGrid}>
        <div className={styles.listColumn}>
          <div className={styles.chipsRow}>
            {STATE_CHIPS.map((chip) => (
              <button
                key={chip.key}
                type="button"
                aria-pressed={states.includes(chip.key)}
                className={`${styles.chip} ${states.includes(chip.key) ? styles.chipOn : ''}`}
                onClick={() => setStates((prev) => toggle(prev, chip.key))}
              >
                {chip.label}
              </button>
            ))}
            <span className={styles.chipDivider} aria-hidden="true" />
            {VISIT_CHIPS.map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed={visits.includes(code)}
                className={`${styles.chip} ${styles.chipMono} ${visits.includes(code) ? styles.chipOn : ''}`}
                onClick={() => setVisits((prev) => toggle(prev, code))}
              >
                {code}
              </button>
            ))}
            <div className={styles.layoutToggle} role="group" aria-label="Layout">
              {LAYOUTS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={layout === key}
                  className={`${styles.layoutButton} ${layout === key ? styles.layoutButtonOn : ''}`}
                  onClick={() => selectLayout(key)}
                >
                  <Icon size={14} aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className={styles.loaderWrap}>
              <CircleLoader color={ColorsNew.darkGreen} strokeWidth={2} circleRadius={12} />
            </div>
          ) : groups.length === 0 ? (
            <div className={styles.emptyState}>No members are due a visit in this scope.</div>
          ) : layout === 'list' ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thMember}>Member</th>
                    <th>Status</th>
                    <th>Due</th>
                    <th>Anchor</th>
                    <th>Clinician · Site</th>
                    <th className={styles.thOpen}>Open</th>
                  </tr>
                </thead>
                {groups.map((group) => (
                  <tbody key={group.ruleId}>
                    <tr className={styles.groupRow}>
                      <td colSpan={6}>
                        {group.label} <span className={styles.groupCount}>{group.rows.length}</span>
                      </td>
                    </tr>
                    {group.rows.map((row) => (
                      <tr
                        key={row.id}
                        className={selectedListClass(row)}
                        onClick={() => setSelectedId(row.id)}
                      >
                        <td className={styles.tdMember}>{row.patient?.name ?? row.patientId}</td>
                        <td>
                          <div className={styles.pills}>
                            <StatusPills row={row} />
                          </div>
                        </td>
                        <td className={styles.tdMono}>
                          {row.dueState === 'held' ? '—' : formatShortDate(row.due)}
                        </td>
                        <td className={`${styles.tdAnchor} ${styles.tdMono}`}>
                          {anchorShort(row)}
                        </td>
                        <td className={styles.tdMuted}>{memberMeta(row)}</td>
                        <td>
                          <OpenButtons row={row} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          ) : (
            <div className={styles.cardsWrap}>
              {groups.map((group) => (
                <div key={group.ruleId} className={styles.cardGroup}>
                  <div className={styles.cardGroupHeader}>
                    {group.label} <span className={styles.groupCount}>{group.rows.length}</span>
                  </div>
                  {group.rows.map((row) => (
                    <div
                      key={row.id}
                      className={`${styles.gapCard} ${selectedClass(row)}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedId(row.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedId(row.id);
                        }
                      }}
                    >
                      <div className={styles.gapCardMain}>
                        <div className={styles.rowName}>{row.patient?.name ?? row.patientId}</div>
                        <p className={styles.gapCardTitle}>{row.title}</p>
                        {row.evidence?.summary && (
                          <p className={styles.gapCardEvidence}>{row.evidence.summary}</p>
                        )}
                        <div className={styles.rowMeta}>{memberMeta(row)}</div>
                      </div>
                      <div className={styles.gapCardSide}>
                        <div className={styles.pills}>
                          <StatusPills row={row} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.railColumn}>
          {selectedRow ? (
            <VisitsDueDetail
              row={selectedRow}
              onDismiss={handleDismiss}
              isDismissing={dismissingId === selectedRow.id}
              onDefer={handleDefer}
              isDeferring={deferringId === selectedRow.id}
              onReopen={handleReopen}
              isReopening={reopeningId === selectedRow.id}
            />
          ) : (
            <div className={styles.railEmpty}>Select a row to see why it is due.</div>
          )}
        </div>
      </div>
    </div>
  );
};

VisitsDueView.propTypes = {
  organizationScope: PropTypes.string,
  departments: PropTypes.array,
  scopeFilters: PropTypes.shape({
    regions: PropTypes.arrayOf(PropTypes.string),
    counties: PropTypes.arrayOf(PropTypes.string),
    markets: PropTypes.arrayOf(PropTypes.string),
    departments: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onChangeScopeFilters: PropTypes.func.isRequired,
  guideEnabled: PropTypes.bool,
  onToggleGuide: PropTypes.func,
  isScopePaused: PropTypes.bool,
  onToggleScopePause: PropTypes.func,
  isPinnedToCurrentScope: PropTypes.bool,
  canResetToPinnedScope: PropTypes.bool,
  showScopeUtilities: PropTypes.bool,
  onToggleScopePin: PropTypes.func,
  onResetScopeToPinned: PropTypes.func,
};

export default VisitsDueView;
